import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import AdinkraMark from "@/components/AdinkraMark";
import { RotateCcw, ArrowRight, Share2, Loader2, Mail, Check } from "lucide-react";
import { renderAdinkraCard, shareOrDownload } from "@/lib/shareCard";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const QS = [
  {
    q: "When a colleague raises something difficult mid-shift, what most often happens?",
    a: [
      { t: "It gets half-heard in the rush, then forgotten", k: "attention" },
      { t: "We hear it, but it never gets written down anywhere", k: "record" },
      { t: "It's logged, but no one decides to act on it", k: "decision" },
      { t: "We decide to act — but nothing actually changes", k: "followthrough" },
    ],
  },
  {
    q: "Where does your team most often lose good information?",
    a: [
      { t: "In the noise — people are too stretched to take it in", k: "attention" },
      { t: "In memory — it lived in one person's head", k: "record" },
      { t: "In limbo — noted, but never owned", k: "decision" },
      { t: "In follow-up — plans made, loops left open", k: "followthrough" },
    ],
  },
  {
    q: "Six weeks after a good training, what's the usual story?",
    a: [
      { t: "People barely remember it happened", k: "attention" },
      { t: "No record of what changed, so we can't tell", k: "record" },
      { t: "Everyone agreed, but no one chose the first step", k: "decision" },
      { t: "It started well, then quietly faded", k: "followthrough" },
    ],
  },
];

const LEAKS = {
  attention: { sym: "akoma", name: "The Attention Leak", line: "Truth is said, but never truly received — drowned by the shift, the noise, the next task.", fix: "Module 1 rebuilds the first thirty seconds so difficult truth actually lands." },
  record: { sym: "hwehwemudua", name: "The Record Leak", line: "Truth is heard, but never written down — so it can't travel beyond the person who heard it.", fix: "Module 6 turns attendance into evidence, so what moved is recorded and travels." },
  decision: { sym: "nkyinkyim", name: "The Decision Leak", line: "Truth is noted, but no one chooses to act — it sits in a system that never decides.", fix: "Modules 4–5 give you the five verbs and the four handovers that force a decision." },
  followthrough: { sym: "nsaa", name: "The Follow-through Leak", line: "Truth is decided on, but nothing changes on the ground — the loop is never closed.", fix: "Module 5 closes the loop, even when the honest answer is no." },
};

export default function LeakDiagnostic() {
  const [step, setStep] = useState(0);
  const [tally, setTally] = useState({});
  const [sharing, setSharing] = useState(false);
  const [email, setEmail] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const done = step >= QS.length;

  const pick = (k) => {
    setTally((t) => ({ ...t, [k]: (t[k] || 0) + 1 }));
    setStep((s) => s + 1);
  };
  const reset = () => { setStep(0); setTally({}); setEmail(""); setEmailSent(false); };
  const winner = Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0] || "attention";
  const result = LEAKS[winner];

  const shareResult = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const blob = await renderAdinkraCard({
        symbol: result.sym,
        kicker: "My dominant leak",
        title: result.name,
        subtitle: result.line,
      });
      await shareOrDownload(blob, "my-truth-leak.png", {
        title: "How Truth Travels",
        text: `${result.name} — the leak that costs my team the most. Find yours: ${window.location.origin}`,
      });
    } catch (e) {
      toast.error("Could not create the card. Try again.");
    } finally {
      setSharing(false);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (emailing || !email) return;
    setEmailing(true);
    try {
      await api.post("/leads", {
        email: email.trim(),
        leak_key: winner,
        leak_name: result.name,
        leak_line: result.line,
      });
      setEmailSent(true);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail) || "Could not send. Check the address and try again.");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <section className="relative bg-wash py-24 sm:py-28 overflow-hidden" data-testid="leak-diagnostic">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-4">A 30-second diagnostic</p>
        <h2 className="font-heading text-purple text-4xl sm:text-5xl leading-[1.08] text-balance">Which leak is yours?</h2>
        <p className="mt-4 text-ink/70 text-[16px]">Truth leaks at four crossings. Answer three quick questions and find the one that costs your team the most.</p>

        <div className="mt-10 rounded-2xl border border-line bg-white p-7 sm:p-9 shadow-[0_14px_40px_rgba(34,20,82,0.08)] min-h-[260px]">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <div className="flex items-center gap-2 mb-5">
                  {QS.map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-8 bg-gold" : i < step ? "w-4 bg-gold/50" : "w-4 bg-line"}`} />
                  ))}
                </div>
                <p className="font-heading text-purple text-2xl leading-snug mb-6">{QS[step].q}</p>
                <div className="grid gap-3">
                  {QS[step].a.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => pick(opt.k)}
                      className="text-left rounded-xl border border-line bg-cream/40 px-5 py-4 text-[15px] text-ink/85 hover:border-gold hover:bg-cream hover:-translate-y-0.5 transition-all duration-200"
                      data-testid={`leak-q${step}-opt${i}`}
                    >
                      {opt.t}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} data-testid="leak-result">
                <div className="flex items-start gap-5">
                  <span className="shrink-0 w-16 h-16 rounded-2xl bg-purple-deep flex items-center justify-center ring-1 ring-gold/40">
                    <AdinkraMark symbol={result.sym} className="text-gold" style={{ width: "34px", height: "34px" }} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Your dominant leak</p>
                    <h3 className="font-heading text-purple text-3xl leading-tight">{result.name}</h3>
                  </div>
                </div>
                <p className="mt-5 text-ink/80 text-[16px] leading-relaxed italic">{result.line}</p>
                <p className="mt-3 text-purple-mid text-[15px] font-semibold">{result.fix}</p>
                <div className="mt-7 flex flex-wrap items-center gap-4">
                  <Link to="/join" className="inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-7 py-3.5 hover:bg-gold-pale transition-colors" data-testid="leak-cta">
                    Plug this leak — £39 <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button onClick={shareResult} disabled={sharing} className="inline-flex items-center gap-2 rounded-full border border-purple-mid/40 text-purple-mid text-[14px] font-semibold px-5 py-3.5 hover:border-gold hover:text-purple transition-colors disabled:opacity-60" data-testid="leak-share">
                    {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    {sharing ? "Preparing…" : "Share my result"}
                  </button>
                  <button onClick={reset} className="inline-flex items-center gap-2 text-purple-mid text-[14px] font-semibold hover:text-purple transition-colors" data-testid="leak-retake">
                    <RotateCcw className="w-4 h-4" /> Retake
                  </button>
                </div>

                <div className="mt-7 pt-6 border-t border-line">
                  {emailSent ? (
                    <p className="inline-flex items-center gap-2 text-[14px] text-purple-mid font-semibold" data-testid="leak-email-sent">
                      <Check className="w-4 h-4 text-gold-dark" /> On its way — check your inbox for your result and a code.
                    </p>
                  ) : (
                    <form onSubmit={sendEmail} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="leak-email-form">
                      <div className="flex items-center gap-2 text-[13px] text-ink/60 sm:mr-1">
                        <Mail className="w-4 h-4 text-gold-dark shrink-0" />
                        <span>Email me my result &amp; a discount code</span>
                      </div>
                      <div className="flex flex-1 gap-2">
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(ev) => setEmail(ev.target.value)}
                          placeholder="you@work-email.com"
                          className="flex-1 rounded-full border border-line bg-cream/50 px-5 py-2.5 text-[14px] text-ink outline-none focus:border-gold transition-colors"
                          data-testid="leak-email-input"
                        />
                        <button
                          type="submit"
                          disabled={emailing}
                          className="inline-flex items-center gap-2 bg-purple-deep text-cream font-bold rounded-full px-5 py-2.5 text-[14px] hover:bg-purple transition-colors disabled:opacity-60 shrink-0"
                          data-testid="leak-email-submit"
                        >
                          {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          Send
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
